/**
 * The Bodhi tree's year (AGENTS.md §7.2, stage B). Pure functions, no Phaser.
 *
 * Ficus religiosa is semi-evergreen: it drops leaves in the dry season, then flushes pink-red
 * new leaves that turn green. Its flowers are hidden inside the figs, so the tree never shows
 * flowers; the seasonal flowers are the lotus in the pond. The calendar below is a design
 * approximation for northern Vietnam, not measured data. Dates follow Vietnam time so every
 * visitor in a room sees the same season. The tree never dies.
 */

export type TreeSeason = {
  /** Share of canopy leaves on the tree, 0–1. */
  density: number;
  /** Colour mix of the leaves on the tree; the four weights sum to 1. */
  old: number; young: number; fresh: number; mature: number;
  /** How often leaves fall, 0–1. */
  fall: number;
  /** Share of fig spots with a pair of figs, and how ripe they are, 0–1. */
  figs: number; ripe: number;
  /** Birds visiting for the figs, 0–1. */
  birds: number;
  /** Lotus flowers in the pond, 0–1. */
  lotus: number;
};
export type SeasonLabel = 'leafFall' | 'flush' | 'fresh' | 'fruit' | 'ripe' | 'green';

type Key = [month: number, day: number, state: Partial<TreeSeason>];
const ZERO: TreeSeason = { density: 1, old: 0, young: 0, fresh: 0, mature: 0, fall: 0, figs: 0, ripe: 0, birds: 0, lotus: 0 };
const KEYS: Key[] = [
  [1, 1, { density: 0.85, old: 0.35, mature: 0.65, fall: 0.55 }],
  [1, 25, { density: 0.6, old: 0.75, mature: 0.25, fall: 1 }], // dry season: old leaves yellow and drop
  [2, 15, { density: 0.28, old: 0.45, young: 0.55, fall: 0.75 }], // briefly almost bare, buds opening
  [3, 1, { density: 0.45, old: 0.1, young: 0.85, fresh: 0.05, fall: 0.3 }], // pink-red new leaves
  [3, 20, { density: 0.78, young: 0.45, fresh: 0.55, fall: 0.15 }],
  [4, 15, { density: 1, fresh: 0.6, mature: 0.4, figs: 0.35, fall: 0.1 }], // green figs in pairs
  [5, 15, { density: 1, fresh: 0.15, mature: 0.85, figs: 0.8, ripe: 0.45, birds: 0.5, lotus: 0.45, fall: 0.1 }],
  [6, 20, { density: 1, mature: 1, figs: 1, ripe: 0.9, birds: 1, lotus: 1, fall: 0.1 }], // ripe figs, birds, lotus
  [7, 31, { density: 1, mature: 1, figs: 0.4, ripe: 1, birds: 0.5, lotus: 0.7, fall: 0.12 }],
  [8, 31, { density: 1, mature: 1, ripe: 1, lotus: 0.15, fall: 0.12 }],
  [10, 15, { density: 0.98, mature: 1, fall: 0.18 }],
  [12, 1, { density: 0.92, old: 0.2, mature: 0.8, fall: 0.35 }],
];
const DAYS_BEFORE = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
const FIELDS = Object.keys(ZERO) as (keyof TreeSeason)[];

/** Position in the year, 0 (1 January) to just under 1. 29 February shares 1 March's place. */
export function yearFraction(month: number, day: number): number {
  return Math.min(364, DAYS_BEFORE[month - 1] + day - 1) / 365;
}
const POINTS = KEYS.map(([month, day, state]) => ({ at: yearFraction(month, day), state: { ...ZERO, ...state } }));

export function treeSeason(fraction: number): TreeSeason {
  const t = ((fraction % 1) + 1) % 1;
  let i = POINTS.length - 1;
  while (i > 0 && POINTS[i].at > t) i--;
  const from = POINTS[i], to = POINTS[(i + 1) % POINTS.length];
  const span = ((to.at - from.at) + 1) % 1 || 1;
  const p = (((t - from.at) + 1) % 1) / span;
  const state = { ...ZERO };
  for (const field of FIELDS) state[field] = from.state[field] + (to.state[field] - from.state[field]) * p;
  return state;
}

/** Today's date in Vietnam, whatever the visitor's own time zone. */
export function vietnamMonthDay(now: Date): { month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Ho_Chi_Minh', month: 'numeric', day: 'numeric' }).formatToParts(now);
  return { month: Number(parts.find(part => part.type === 'month')!.value), day: Number(parts.find(part => part.type === 'day')!.value) };
}

/** `?date=2026-02-20` or `?date=02-20` previews another day of the tree's year (visual only). */
export function dateOverride(search: string): { month: number; day: number } | null {
  const match = /^(?:\d{4}-)?(\d{1,2})-(\d{1,2})$/.exec(new URLSearchParams(search).get('date') ?? '');
  if (!match) return null;
  const month = Number(match[1]), day = Number(match[2]);
  return month >= 1 && month <= 12 && day >= 1 && day <= 31 ? { month, day } : null;
}

export function seasonLabel(state: TreeSeason): SeasonLabel {
  if (state.fall >= 0.6 && state.old >= 0.4) return 'leafFall';
  if (state.young >= 0.4) return 'flush';
  if (state.figs >= 0.3 && state.ripe >= 0.6) return 'ripe';
  if (state.figs >= 0.3) return 'fruit';
  if (state.fresh >= 0.4) return 'fresh';
  return 'green';
}

/** Leaf colours by age; multiplied by each leaf's own shade and the chosen sky's light. */
export const LEAF_COLORS = { old: 0xc9a85a, young: 0xc8826e, fresh: 0xa6bd6c, mature: 0x5b7c4f } as const;
export const LEAF_AGES = ['old', 'young', 'fresh', 'mature'] as const;
export type LeafAge = typeof LEAF_AGES[number];

/** Which age a leaf shows, from its stable random `u` in [0, 1) and today's colour mix. */
export function leafAge(state: TreeSeason, u: number): LeafAge {
  let edge = 0;
  for (const age of LEAF_AGES) { edge += state[age]; if (u < edge) return age; }
  return 'mature';
}

/** Fig colour: green, then purple, then deep purple when fully ripe. */
export function figColor(ripeness: number): number {
  const r = Math.max(0, Math.min(1, ripeness));
  return r < 0.6 ? mix(0x8fa05a, 0x7a3f62, r / 0.6) : mix(0x7a3f62, 0x4a2642, (r - 0.6) / 0.4);
}

export function mix(a: number, b: number, t: number): number {
  const channel = (shift: number) => Math.round(((a >> shift) & 255) + ((((b >> shift) & 255) - ((a >> shift) & 255)) * t));
  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
}
/** Multiplies two colours channel by channel, like a Phaser tint. */
export function multiply(a: number, b: number): number {
  const channel = (shift: number) => Math.round((((a >> shift) & 255) * ((b >> shift) & 255)) / 255);
  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
}
export function brighten(color: number, factor: number): number {
  const channel = (shift: number) => Math.min(255, Math.round(((color >> shift) & 255) * factor));
  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
}

/** Stable pseudo-random value in [0, 1) for an index, so each leaf keeps its own character. */
export function hash01(index: number, salt = 0): number {
  let x = (index + 1) * 374761393 + salt * 668265263;
  x = Math.imul(x ^ (x >>> 13), 1274126177);
  return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
}

/** Average colour of today's leaves, for the soft canopy mass behind them. */
export function canopyColor(state: TreeSeason): number {
  const total = LEAF_AGES.reduce((sum, age) => sum + state[age], 0) || 1;
  const rgb = [0, 0, 0];
  for (const age of LEAF_AGES) {
    const weight = state[age] / total, color = LEAF_COLORS[age];
    rgb[0] += ((color >> 16) & 255) * weight; rgb[1] += ((color >> 8) & 255) * weight; rgb[2] += (color & 255) * weight;
  }
  const [r, g, b] = rgb.map(value => Math.min(255, Math.round(value)));
  return (r << 16) | (g << 8) | b;
}

/** Colours of leaves that fall: mostly old, yellowing leaves. */
export const FALLING_LEAF_COLORS = [LEAF_COLORS.old, 0xb08a4a, 0xd1b56a, 0x9fa35e] as const;
